import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Drill, Factory, Fuel, Cloud } from 'lucide-react';
import { useTheme } from "../../../../context/ThemeContext";

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const { isDarkTheme } = useTheme();

  const questions = [
    {
      question: "How does OGFLEET optimize drilling trajectory in complex formations?",
      answer: "OGFLEET uses real-time data analysis with machine learning to adjust drilling parameters...",
      category: 'drilling',
      icon: <Drill className="w-6 h-6" />
    },
    {
      question: "Can OGFLEET predict reservoir performance under different extraction scenarios?",
      answer: "Our AI models simulate reservoir behavior using historical data...",
      category: 'reservoir',
      icon: <Fuel className="w-6 h-6" />
    },
    {
      question: "How does OGFLEET assist in petroleum production?",
      answer: "OGFLEET optimizes drilling operations and production schedules, ensuring efficient resource extraction and minimizing downtime..",
      category: 'production',
      icon: <Factory className="w-6 h-6" />
    },
    {
      question: "Can OGFLEET integrate data across drilling, reservoir, and manufacturing?",
      answer: "Yes, our unified platform connects all three domains...",
      category: 'all',
      icon: <Cloud className="w-6 h-6" />
    },
    {
      question: "Does OGFLEET help mitigate drilling risks in unstable geological formations?",
      answer: "Our system employs predictive analytics to identify potential instability zones...",
      category: 'drilling',
      icon: <Drill className="w-6 h-6" />
    },
    {
      question: "How does OGFLEET enhance hydrocarbon recovery from mature reservoirs?",
      answer: "Through AI-driven analysis of saturation patterns and pressure data...",
      category: 'reservoir',
      icon: <Fuel className="w-6 h-6" />
    },
    {
      question: "Does OGFLEET improve production monitoring in petroleum extraction?",
      answer: "Advanced data analytics and IoT sensors track real-time production rates and reservoir conditions, enhancing decision-making for optimal performance...",
      category: 'production',
      icon: <Factory className="w-6 h-6" />
    },
    {
      question: "How does OGFLEET reduce environmental impact across operations?",
      answer: "Our platform optimizes resource usage and monitors emissions...",
      category: 'all',
      icon: <Cloud className="w-6 h-6" />
    },
  ];

  const CategoryPill = ({ name, icon }) => (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={`px-6 py-2 rounded-full flex items-center gap-2 ${
        activeCategory === name.toLowerCase()
          ? isDarkTheme
            ? 'bg-amber-600 text-white'
            : 'bg-amber-500 text-white'
          : isDarkTheme
            ? 'bg-gray-800 text-amber-400'
            : 'bg-amber-100 text-amber-600'
      }`}
      onClick={() => setActiveCategory(name.toLowerCase())}
    >
      {icon}
      <span>{name}</span>
    </motion.button>
  );

  return (
    <section 
      id="faqs" 
      className={`${isDarkTheme ? 'bg-black' : 'bg-white'} py-24 px-4 relative`}
    >
      {/* Floating Elements */}
      <div className={`absolute right-20 top-1/4 w-24 h-24 ${
        isDarkTheme ? 'bg-amber-900/10' : 'bg-amber-500/10'
      } blur-3xl animate-float`}></div>
      <div className={`absolute left-32 top-1/3 w-32 h-32 ${
        isDarkTheme ? 'bg-amber-900/10' : 'bg-amber-500/10'
      } blur-3xl animate-float-delayed`}></div>

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-3xl font-bold mb-7 ${
              isDarkTheme ? 'text-amber-400' : 'text-amber-600'
            }`}
          >
            Intelligent Solutions Across Domains
          </motion.h2>

          <div className="flex flex-wrap gap-4 justify-center mb-12">
            <CategoryPill name="All" icon={<Cloud size={18} />} />
            <CategoryPill name="Drilling" icon={<Drill size={18} />} />
            <CategoryPill name="Reservoir" icon={<Fuel size={18} />} />
            <CategoryPill name="Production" icon={<Factory size={18} />} />
          </div>
        </div>

        <motion.div
          layout
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          <AnimatePresence>
            {questions
              .filter(q => activeCategory === 'all' || q.category === activeCategory)
              .map((item, index) => (
                <motion.div
                  key={index}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className={`group rounded-xl p-6 ${
                    isDarkTheme
                      ? 'bg-gray-900 border-gray-700 hover:bg-gray-800'
                      : 'bg-white border-gray-200 hover:bg-amber-50'
                  } border shadow-lg hover:shadow-xl transition-all`}
                >
                  <div className="flex gap-4">
                    <div className={`p-3 rounded-xl ${
                      isDarkTheme
                        ? 'bg-gray-800 text-amber-400'
                        : 'bg-amber-100 text-amber-600'
                    }`}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold mb-2 ${
                        isDarkTheme ? 'text-white/90' : 'text-gray-800'
                      }`}>
                        {item.question}
                      </h3>
                      <motion.p
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        className={`${
                          isDarkTheme ? 'text-gray-300' : 'text-gray-600'
                        } overflow-hidden`}
                      >
                        {item.answer}
                      </motion.p>
                    </div>
                  </div>
                  {/* Hover Border */}
                  <div className={`absolute inset-0 border-2 border-transparent group-hover:${
                    isDarkTheme 
                      ? 'border-amber-400/20' 
                      : 'border-amber-600/20'
                  } rounded-xl transition-all pointer-events-none`} />
                </motion.div>
              ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;